import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import EntityPicker from "./EntityPicker";
import OperativePicker from "./OperativePicker";
import { todayLocalISO } from "../../lib/dateUtil";

const EMPTY = { topic: "", points: "", delivered_at: "", delivered_by_name: "", location: "", notes: "", attendees: [], entity_type: "", entity_id: "", entity_title: "" };

/** Record a toolbox talk: pick a library topic (points prefilled) or write your own; attendees acknowledge in their portal
 *  unless the talk was delivered face-to-face and marked as briefed here. */
export default function ToolboxTalkDialog({ open, onOpenChange, talk, topics, onSaved, mode = "edit" }) {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [briefedNow, setBriefedNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const editing = !!talk?.id;
  const issuing = editing && mode === "issue" && talk?.status === "draft";
  const isDraft = editing && talk?.status === "draft";

  useEffect(() => {
    if (!open) return;
    setBriefedNow(false);
    setF(talk
      ? { ...EMPTY, ...talk, points: (talk.points || []).join("\n"), delivered_at: talk.delivered_at || (talk.status === "draft" ? todayLocalISO() : ""), delivered_by_name: talk.delivered_by_name || (talk.status === "draft" ? (user?.name || "") : "") }
      : { ...EMPTY, delivered_at: todayLocalISO(), delivered_by_name: user?.name || "" });
  }, [open, talk, user]);

  const set = (patch) => setF(x => ({ ...x, ...patch }));
  const applyTopic = (t) => { if (!t) return; set({ topic: t.topic, points: f.points || t.points.join("\n") }); };

  const save = async () => {
    if (!f.topic.trim()) return toast.error("Topic is required");
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const attendees = f.attendees.map(a => ({ user_id: a.user_id, name: a.name, acknowledged_at: briefedNow ? (a.acknowledged_at || now) : (a.acknowledged_at || "") }));
      const { status, source, ...rest } = f;
      const body = { ...rest, points: f.points.split("\n").map(s => s.trim()).filter(Boolean), attendees };
      const { data } = issuing ? await API.post(`/hs/toolbox-talks/${talk.id}/issue`, body)
        : editing ? await API.put(`/hs/toolbox-talks/${talk.id}`, body) : await API.post("/hs/toolbox-talks", body);
      toast.success(issuing ? `${data.reference} issued${briefedNow || !attendees.length ? "" : " — attendees notified"}` : editing ? "Toolbox talk updated" : `${data.reference} recorded${briefedNow ? "" : " — attendees notified"}`);
      onSaved(data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="toolbox-talk-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">{issuing ? `Issue ${talk.reference}` : editing ? `Edit ${talk.reference}` : "Record a toolbox talk"}</DialogTitle></DialogHeader>
        {isDraft && (
          <p className="text-[11px] rounded-md bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-900/60 px-3 py-2 text-violet-900 dark:text-violet-200 inline-flex items-start gap-1.5" data-testid="talk-draft-note">
            <Sparkle size={12} weight="fill" className="mt-0.5 shrink-0" />
            <span>AI draft written from {talk.source?.rams_reference || "the RAMS"}. {issuing ? "Check the points, pick who attends, then issue — attendees acknowledge it in their portal unless you mark it delivered face-to-face." : "Saving keeps it as a draft; use Issue when you're ready to deliver it."}</span>
          </p>
        )}
        <div>
          <Label className="text-[11px]">Library topic</Label>
          <select onChange={e => applyTopic(topics.find(t => t.topic === e.target.value))} defaultValue="" className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-2" data-testid="talk-library-topic">
            <option value="">Pick a topic to prefill the talking points…</option>
            {topics.map(t => <option key={t.topic} value={t.topic}>{t.topic}</option>)}
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label className="text-[11px]">Topic</Label><Input value={f.topic} onChange={e => set({ topic: e.target.value })} className="mt-1 h-9 text-sm" data-testid="talk-topic" /></div>
          <div><Label className="text-[11px]">Delivered on</Label><Input type="date" value={f.delivered_at} onChange={e => set({ delivered_at: e.target.value })} className="mt-1 h-9 text-sm" data-testid="talk-delivered-at" /></div>
          <div><Label className="text-[11px]">Delivered by</Label><Input value={f.delivered_by_name} onChange={e => set({ delivered_by_name: e.target.value })} className="mt-1 h-9 text-sm" data-testid="talk-delivered-by" /></div>
          <div className="md:col-span-2"><Label className="text-[11px]">Location</Label><Input value={f.location} onChange={e => set({ location: e.target.value })} placeholder="Depot, site address or 'remote'" className="mt-1 h-9 text-sm" data-testid="talk-location" /></div>
        </div>
        <div><Label className="text-[11px]">Talking points (one per line)</Label><Textarea value={f.points} onChange={e => set({ points: e.target.value })} rows={6} className="mt-1 text-xs" data-testid="talk-points" /></div>
        <div>
          <Label className="text-[11px]">Attendees</Label>
          <div className="mt-1"><OperativePicker value={f.attendees} onChange={v => set({ attendees: v })} testPrefix="talk-attendees" /></div>
          <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
            <input type="checkbox" checked={briefedNow} onChange={e => setBriefedNow(e.target.checked)} data-testid="talk-briefed-now" />
            Delivered face-to-face — record all attendees as briefed now (otherwise they acknowledge in their portal)
          </label>
        </div>
        <details className="border border-border rounded-md">
          <summary className="cursor-pointer text-xs px-3 py-2 font-medium" data-testid="talk-link-job-toggle">
            Linked job: {f.entity_id ? <span className="text-primary">{f.entity_title || f.entity_id}</span> : <span className="text-muted-foreground">none (optional)</span>}
          </summary>
          <div className="p-3 border-t border-border">
            <EntityPicker compact onPick={(p) => set({ entity_type: p.entity_type, entity_id: p.entity_id, entity_title: p.title })} />
            {f.entity_id && <button type="button" onClick={() => set({ entity_type: "", entity_id: "", entity_title: "" })} className="text-[11px] text-red-600 hover:underline mt-2">Unlink job</button>}
          </div>
        </details>
        <div><Label className="text-[11px]">Notes / questions raised</Label><Textarea value={f.notes} onChange={e => set({ notes: e.target.value })} rows={2} className="mt-1 text-xs" data-testid="talk-notes" /></div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} className={issuing ? "bg-violet-600 hover:bg-violet-700 text-white" : ""} data-testid="talk-save">{busy ? (issuing ? "Issuing…" : "Saving…") : issuing ? "Issue talk" : editing ? (isDraft ? "Save draft" : "Save changes") : "Record talk"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
